import type { QueryClient } from '@tanstack/react-query';
import type { useGT } from 'gt-react';
import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import {
  loadFulfillmentCandidatesByLine,
  planBulkFulfillmentOverrides,
  type BulkOverrideForm,
} from './bulkFulfillmentOverride';
import {
  bulkOverrideHomogeneousSkipReason,
  bulkOverrideMultiSkipToastMessage,
  bulkOverrideSkipLabel,
  bulkOverrideSkipReasonText,
  bulkOverrideSkippedSkuList,
} from './bulkOverrideSkipMessages';
import type { BulkOverrideLineResult } from './bulkFulfillmentOverride';
import { settledMutationCounts } from './settledMutationCounts';

type Gt = ReturnType<typeof useGT>;

export function reportBulkOverrideOutcome(
  gt: Gt,
  bulkLineCount: number,
  applied: number,
  skipped: number,
  failed: number,
  skippedResults: ReadonlyArray<BulkOverrideLineResult> = [],
  lines: readonly AssignmentLine[] = [],
): void {
  if (applied > 0) {
    toast.success(
      gt('Applied to {applied} of {total} selected lines.', {
        context: 'bulk fulfillment override success toast',
        applied,
        total: bulkLineCount,
      }),
    );
  }
  if (skipped > 0) {
    const skippedOnly = skippedResults.filter((r) => r.status === 'skipped');
    if (skipped === 1 && skippedOnly.length === 1) {
      const row = skippedOnly[0];
      const line = lines.find((l) => l.suggestionId === row.suggestionId);
      toast.error(
        gt('Skipped 1 line ({sku}): {reason}', {
          context: 'bulk fulfillment override single skip toast',
          sku: bulkOverrideSkipLabel(line),
          reason: bulkOverrideSkipReasonText(gt, row.reason),
        }),
      );
    } else {
      const skippedTyped = skippedOnly.filter(
        (r): r is Extract<BulkOverrideLineResult, { status: 'skipped' }> =>
          r.status === 'skipped',
      );
      const reasonKey = bulkOverrideHomogeneousSkipReason(skippedTyped);
      const skus = bulkOverrideSkippedSkuList(skippedTyped, lines);
      if (reasonKey != null && skus !== '') {
        toast.error(
          bulkOverrideMultiSkipToastMessage(gt, skipped, skus, reasonKey),
        );
      } else {
        toast.error(
          gt(
            'Skipped {n} lines (vendor or source not available for that item).',
            {
              context: 'bulk fulfillment override skipped toast',
              n: skipped,
            },
          ),
        );
      }
    }
  }
  if (failed > 0) {
    toast.error(
      gt('Could not save {n} lines. Try again.', {
        context: 'bulk fulfillment override failed toast',
        n: failed,
      }),
    );
  }
}

export async function executePlannedBulkOverrideMutations(
  gt: Gt,
  bulkLineCount: number,
  lines: readonly AssignmentLine[],
  planned: ReadonlyArray<BulkOverrideLineResult>,
  mutateAsync: (input: OverrideOptionInput) => Promise<unknown>,
): Promise<{ applied: number; skipped: number; failed: number }> {
  const toApply = planned.filter((p) => p.status === 'applied');
  const skipped = planned.filter((p) => p.status === 'skipped').length;
  const { applied, failed } = await settledMutationCounts(toApply, (p) =>
    mutateAsync(p.input),
  );
  reportBulkOverrideOutcome(
    gt,
    bulkLineCount,
    applied,
    skipped,
    failed,
    planned,
    lines,
  );
  return { applied, skipped, failed };
}

export async function applyBulkFulfillmentOverrides(
  queryClient: QueryClient,
  gt: Gt,
  lines: readonly AssignmentLine[],
  bulkLineCount: number,
  form: BulkOverrideForm,
  mutateAsync: (input: OverrideOptionInput) => Promise<unknown>,
): Promise<{ applied: number; skipped: number; failed: number }> {
  const candidatesByLine = await loadFulfillmentCandidatesByLine(
    queryClient,
    lines,
  );
  const planned = planBulkFulfillmentOverrides(lines, form, candidatesByLine);
  return executePlannedBulkOverrideMutations(
    gt,
    bulkLineCount,
    lines,
    planned,
    mutateAsync,
  );
}
